import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import type { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex';
import '@adornis/chemistry/elements/components/x-section';
import '@adornis/chemistry/elements/components/x-tabs';
import {
  buildFullOptions,
  type DataViewOptions,
  type FullDataViewOptions,
} from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/filter/client/x-filter-editor.js';
import '@adornis/popover/x-dropdown-selection';
import { html } from 'lit';
import { BuddyExportDataView } from '../db/BuddyExportDataView.js';
import { BuddyExportHiddenFields } from '../static/BuddyExportHiddenFields.js';

// { dataViewOptions, filterOptions } =
export function buildBuddyExportBuildOptions(
  dataViewOptions: DataViewOptions<typeof BuddyUser>,
  targetTimeframe?: NarrowedEntity<Timeframe, { _id: 1 }>,
) {
  const opt: DataViewOptions<typeof BuddyUser> = {
    class: BuddyUser,
    isColumnSelectable: true,
    view: new View<typeof BuddyUser>({
      filter: new AdornisFilter({
        searchFields: ['email'],
        aspects: [],
      }),
    }),
    customColumns: [
      { fieldName: 'email' },
      {
        fieldName: '_class',
        filterAspect: {
          aspectConstructor: StringAspectConfig('equals').aspectConstructor,
          renderEditor: () => html`
            <x-dropdown-selection
              .selectables=${[BuddyUserMentee._class, BuddyUserMentor._class]}
            ></x-dropdown-selection>
          `,
        },
      },
    ],
    fieldSelectionOptions: {
      shouldIncludeField: bqlFieldDef => !BuddyExportHiddenFields.includes(bqlFieldDef.name),
    },
    onViewChanged: () => null,
    emptyState: () => html`<tr-text>Keine Einträge</tr-text>`,

    ...dataViewOptions,
  };

  const timeframeAspect = (opt.view?.filter?.aspects ?? []).find(aspect => aspect.fieldName === 'timeframeID');

  if (targetTimeframe && (!timeframeAspect || timeframeAspect.value !== targetTimeframe._id)) {
    const newAspect =
      timeframeAspect ??
      new (StringAspectConfig('equals').aspectConstructor)({
        mode: 'equals',
        value: null,
        fieldName: 'timeframeID',
      });

    newAspect.value = targetTimeframe._id;

    if (!timeframeAspect) {
      opt.view?.filter?.aspects.push(newAspect);
    }
  }

  return buildFullOptions(opt, {
    overridesForFieldName: { timeframeID: StringAspectConfig('equals') },
    fieldAspectGeneration: {
      shouldIncludeField: bqlFieldDef => !BuddyExportHiddenFields.includes(bqlFieldDef.name),
    },
  });
}

export const buddyExportDataView = async <T extends typeof AdornisEntity, Te extends typeof BuddyExportDataView>(
  dataViewOptions: FullDataViewOptions<T>,
  exportOptions?: {
    fileName?: string;
    encoding?: 'windows1252' | 'utf8';
    stepSize?: number;
    exportClass?: Te;
  },
) => {
  return exportDataView(dataViewOptions, { exportClass: BuddyExportDataView, ...exportOptions });
};

export namespace BuddyExportAPI {
  export const buildOptions = buildBuddyExportBuildOptions;
  export const exportData = buddyExportDataView;
}
